import { FilterUi } from "./FilterUi.js";
import { CheckboxGroup } from "../../../ui/CheckboxGroup.js"
import { FilterSettings } from "../FilterSettings.js";

export { CheckboxFilter };

/**
 * A class that defines the UI for the CheckboxGroup Attribute Filter
 * 
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @author rhess <robin.hess@awi.de> 
 *
 * @memberof vef.map.filters.ui
 */
class CheckboxFilter extends FilterUi {

    /**
     * @param {HTMLElement | string} target
     * @param {object} options filter specific options
     * @param {LayerCache} layers Used for included/excluded layers
     */
    constructor(target, options, layers) {

        // apply default options
        options = Object.assign({
            title: "Checkbox Filter",
            allowShowNullValues: true,
            showNullValues: true,
            multiColumn: true,
            sortFields: true,
            fields: []
        }, options || {});

        super(target, options, layers);

        // sort values alphabetically
        if (options.sortFields) options.fields.sort((a, b) => a.title.localeCompare(b.title));

        this.getElement().classList.add("checkbox-filter");
        this.validateFields_(options.fields);

        this.settingsClass_ = CheckboxFilterSettings;
        this.checkBoxGroup_ = this.initCheckboxGroup_({
            nodes: options.fields,
            singleSelect: options.singleSelect,
            multiColumn: options.multiColumn
        });

        this.setTitle(options.title);
        this.addToolButtons_();
        this.initShowNullButton_();
    }

    /**
     * Initiates the Checkbox Group for Attribute Filter
     * 
     * @param {object} options 
     */
    initCheckboxGroup_(options) {
        const group = new CheckboxGroup(this.getContentContainer(), options);

        group.on("changed", (e) => {
            this.fire("change", this);
        });

        return group;
    }

    /**
     * Validates the fields
     */
    validateFields_(fields) {
        for (let i = 0; i < fields.length; ++i) {
            const field = fields[i];
            if (!field.type) field.type = "string";
            if (!field.operator) field.operator = "eq";
        }
    }

    /**
     * @private
     */
    addToolButtons_() {
        this.addTool("vef vef-deselect-all", () => this.checkBoxGroup_.deselectAll(), "Deselect All");
        if (!this.options_.singleSelect) this.addTool("vef vef-select-all", () => this.checkBoxGroup_.selectAll(), "Select All");
    }

    /**
     * Returns the active values in the Attribute Filter
     * 
     * @returns values
     */
    getActiveValues() {
        const selectedItems = this.checkBoxGroup_.getSelected();
        const filterValues = [];

        for (let i = 0; i < selectedItems.length; ++i) {
            const item = selectedItems[i];
            const column = item.config.column;
            const operator = item.config.operator;
            let values = item.config.values;

            if (!Array.isArray(values)) values = [values];
            for (let i = 0; i < values.length; ++i) {
                filterValues.push([operator, column, values[i]]);
            }

        }

        return filterValues;
    }

    /**
     * Returns the Active Filter Values
     * 
     * @override
     * @returns {object} Filter Object
     */
    getActiveFilter() {
        const activeAttributeFilter = this.getActiveValues();
        let activeFilter = {};

        for (let i = 0; i < activeAttributeFilter.length; ++i) {
            const activeItem = activeAttributeFilter[i];

            if (activeItem[0] === 'eq' && activeItem.length === 2) {
                // TODO
            } else if (activeItem[0] === 'eq' && activeItem.length === 3) {
                if (!activeFilter.hasOwnProperty('values')) {
                    activeFilter.values = [];
                }
                activeFilter.column = activeItem[1];
                activeFilter.type = "attribute";
                activeFilter.values.push([activeItem[0], activeItem[2]]);
            }
        }
        activeFilter.excludedLayers = this.getExcludedLayers();

        if (activeFilter.column && (activeFilter.values.length > 0) && this.options_.showNullValues) activeFilter.values.push(["null", ""]);

        return activeFilter;
    }

    /**
     * Get the filter's options-object
     */
    getOptions() {
        const options = Object.assign({}, this.options_);
        options.fields = this.checkBoxGroup_.nodes.map(node => node.config);

        return options;
    }

    /**
     * method to reload options after applying settings
     */
    reloadOptions_() {
        this.setTitle(this.options_.title);
        this.toggleToolVisibility("btn-power", this.options_.deactivatable);
        this.toggleToolVisibility("btn-show-null", this.options_.allowShowNullValues);
        this.fire("change", this);
    }

}

class CheckboxFilterSettings extends FilterSettings {

    htmlExtension = `
        <div><label><input type="checkbox" name="allow-show-null-values"/> Show "include NULL values" Button</label></div>
    `;

    /**
     * @param {FilterUi} filter
     */
    constructor(filter) {
        super(filter);

        this.query("form").insertAdjacentHTML("beforeend", this.htmlExtension);
    }

    /**
     * Get the currently configured filter options
     */
    getFilterOptions() {
        const options = super.getFilterOptions();

        options.allowShowNullValues = this.query("form input[name='allow-show-null-values']").checked;

        return options;
    }

    /**
     * Update Ui based on the filters current options
     */
    updateFilterOptions() {
        super.updateFilterOptions();
        const options = this.filter.options_;

        this.query("form input[name='allow-show-null-values']").checked = options.allowShowNullValues;
    }

}